import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { realtimeValidation } from '../../../utils/validationUtils';

const BuyerPreferencesModal = ({ isOpen, onClose, onSavePreferences }) => {
  const [preferences, setPreferences] = useState({
    propertyType: '',
    location: '',
    budgetMin: '',
    budgetMax: '',
    bedrooms: '',
    bathrooms: '',
    minArea: '',
    maxArea: '',
    furnishing: '',
    parking: false,
    pool: false,
    gym: false,
    garden: false,
    seaView: false,
    paymentPlan: '',
    readyToMove: '',
    notes: ''
  });
  const [validationErrors, setValidationErrors] = useState({});

  const handleChange = (field, value) => {
    setPreferences(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (validationErrors?.[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        return newErrors;
      });
    }
    
    // Real-time validation
    const error = realtimeValidation?.validateField(field, value, { ...preferences, [field]: value });
    if (error) {
      setValidationErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleBlur = (field) => {
    const value = preferences?.[field];
    const error = realtimeValidation?.validateOnBlur(field, value, false);
    if (error) {
      setValidationErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleCheckbox = (field) => {
    setPreferences(prev => ({ ...prev, [field]: !prev?.[field] }));
  };

  const handleSave = () => {
    // Validate budget range
    const errors = {};
    if (preferences?.budgetMin && preferences?.budgetMax) {
      const min = parseFloat(preferences?.budgetMin);
      const max = parseFloat(preferences?.budgetMax);
      if (!isNaN(min) && !isNaN(max) && min > max) {
        errors.budgetRange = 'الحد الأدنى للميزانية يجب أن يكون أقل من الحد الأقصى';
      }
    }
    
    if (Object.keys(errors)?.length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    onSavePreferences?.(preferences);
    onClose();
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="تفضيلات المشترين" 
      size="lg"
      footer={null}
      icon={null}
      iconColor={null}
    >
      <div className="space-y-4 max-h-[70vh] overflow-y-auto">
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 text-purple-400">
            <Icon name="ShoppingCart" size={20} />
            <span className="font-semibold">تحديد متطلبات الشراء</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">نوع العقار</label>
            <Select
              value={preferences?.propertyType}
              onChange={(e) => handleChange('propertyType', e?.target?.value)}
            >
              <option value="">اختر النوع</option>
              <option value="apartment">شقة</option>
              <option value="villa">فيلا</option>
              <option value="townhouse">تاون هاوس</option>
              <option value="penthouse">بنتهاوس</option>
              <option value="duplex">دوبلكس</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">الموقع المفضل</label>
            <Input
              placeholder="مثال: دبي مارينا"
              value={preferences?.location}
              onChange={(e) => handleChange('location', e?.target?.value)}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">الميزانية (دينار أردني)</label>
          <div className="grid grid-cols-2 gap-4">
            <Input
              type="number"
              placeholder="الحد الأدنى"
              value={preferences?.budgetMin}
              onChange={(e) => handleChange('budgetMin', e?.target?.value)}
            />
            <Input
              type="number"
              placeholder="الحد الأقصى"
              value={preferences?.budgetMax}
              onChange={(e) => handleChange('budgetMax', e?.target?.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">غرف النوم</label>
            <Select
              value={preferences?.bedrooms}
              onChange={(e) => handleChange('bedrooms', e?.target?.value)}
            >
              <option value="">اختر العدد</option>
              <option value="studio">استوديو</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5+">5+</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">دورات المياه</label>
            <Select
              value={preferences?.bathrooms}
              onChange={(e) => handleChange('bathrooms', e?.target?.value)}
            >
              <option value="">اختر العدد</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5+">5+</option>
            </Select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">المساحة (قدم مربع)</label>
          <div className="grid grid-cols-2 gap-4">
            <Input
              type="number"
              placeholder="الحد الأدنى"
              value={preferences?.minArea}
              onChange={(e) => handleChange('minArea', e?.target?.value)}
            />
            <Input
              type="number"
              placeholder="الحد الأقصى"
              value={preferences?.maxArea}
              onChange={(e) => handleChange('maxArea', e?.target?.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">الأثاث</label>
            <Select
              value={preferences?.furnishing}
              onChange={(e) => handleChange('furnishing', e?.target?.value)}
            >
              <option value="">غير محدد</option>
              <option value="furnished">مفروش</option>
              <option value="unfurnished">غير مفروش</option>
              <option value="semi-furnished">نصف مفروش</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">خطة الدفع</label>
            <Select
              value={preferences?.paymentPlan}
              onChange={(e) => handleChange('paymentPlan', e?.target?.value)}
            >
              <option value="">غير محدد</option>
              <option value="cash">نقدي</option>
              <option value="installment">تقسيط</option>
              <option value="mortgage">رهن عقاري</option>
            </Select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">المرافق المطلوبة</label>
          <div className="grid grid-cols-2 gap-3">
            <Checkbox
              label="موقف سيارات"
              checked={preferences?.parking}
              onChange={() => handleCheckbox('parking')}
            />
            <Checkbox
              label="مسبح"
              checked={preferences?.pool}
              onChange={() => handleCheckbox('pool')}
            />
            <Checkbox
              label="صالة رياضية"
              checked={preferences?.gym}
              onChange={() => handleCheckbox('gym')}
            />
            <Checkbox
              label="حديقة"
              checked={preferences?.garden}
              onChange={() => handleCheckbox('garden')}
            />
            <Checkbox
              label="إطلالة بحرية"
              checked={preferences?.seaView}
              onChange={() => handleCheckbox('seaView')}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">جاهز للانتقال</label>
          <Select
            value={preferences?.readyToMove}
            onChange={(e) => handleChange('readyToMove', e?.target?.value)}
          >
            <option value="">غير محدد</option>
            <option value="immediate">فوري</option>
            <option value="1-3-months">1-3 أشهر</option>
            <option value="3-6-months">3-6 أشهر</option>
            <option value="6-12-months">6-12 شهر</option>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">ملاحظات إضافية</label>
          <textarea
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            rows="3"
            placeholder="أي متطلبات أو تفضيلات إضافية..."
            value={preferences?.notes}
            onChange={(e) => handleChange('notes', e?.target?.value)}
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button onClick={handleSave} className="flex-1">
            <Icon name="Save" size={18} className="ml-2" />
            حفظ التفضيلات
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default BuyerPreferencesModal;